import { Injectable } from '@angular/core';
import { MerchandiseService } from '@epicuro-next/data/merchandise';
import {
  AccountsConfigurationKeys,
  AccountsConfigurations,
  ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES,
  ACCOUNTS_FEATURE_NAME,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import { LocalStorageService } from '@epicuro-next/platform/storage';
import { isNonNullish } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

interface State {
  backgroundUrl?: string;
}

const BG_KEY = 'account-selector-background';

@Injectable()
export class MerchandiseBackgroundComponentStore extends ComponentStore<State> {
  private enabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        AccountsConfigurationKeys.ShowMerchandiseBackground,
        AccountsConfigurations
      >(
        ACCOUNTS_FEATURE_NAME,
        AccountsConfigurationKeys.ShowMerchandiseBackground,
        ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES[
          AccountsConfigurationKeys.ShowMerchandiseBackground
        ],
      ),
    ),
  );

  public backgroundUrl$ = combineLatest([
    this.select((s) => s.backgroundUrl),
    this.enabled$,
  ]).pipe(map(([url, enabled]) => (enabled ? url : undefined)));

  private storage: LocalStorageService;

  constructor(
    private store: Store,
    private merchandiseService: MerchandiseService,
    storage: LocalStorageService,
  ) {
    super({ backgroundUrl: storage.get(BG_KEY) });

    this.storage = storage;
    this.getBackgroundUrl(this.enabled$);
  }

  private getBackgroundUrl = this.effect<boolean | undefined>((enabled$) =>
    enabled$.pipe(
      filter(isNonNullish),
      switchMap((enabled) =>
        enabled
          ? this.merchandiseService.getAccountSelectorBackground()
          : of(undefined),
      ),
      tapResponse(
        (backgroundUrl: string) => {
          this.patchState({ backgroundUrl });
          this.storage.set(BG_KEY, backgroundUrl);
        },
        () => {
          this.patchState({ backgroundUrl: undefined });
          this.storage.remove(BG_KEY);
        },
      ),
    ),
  );
}
